import type { UIMessage } from 'ai';
import { useTranslation } from 'react-i18next';

interface UserMessageProps {
  message: UIMessage;
}

/**
 * Right-aligned chat bubble for user messages.
 * Images render in a separate row above the text bubble (no background).
 * Text renders in the standard primary-colored bubble below.
 */
export function UserMessage({ message }: UserMessageProps) {
  const { t } = useTranslation();
  const textParts = message.parts.filter(
    (p): p is Extract<(typeof message.parts)[number], { type: 'text' }> => p.type === 'text',
  );
  const fileParts = message.parts.filter(
    (p): p is { type: 'file'; mediaType: string; url: string; filename?: string } =>
      p.type === 'file',
  );

  const text = textParts.map((p) => p.text).join('');

  if (!text && fileParts.length === 0) return null;

  return (
    <div className="flex flex-col items-end gap-1.5" data-testid="user-message">
      {fileParts.length > 0 && (
        <div className="flex max-w-[85%] justify-end gap-1.5">
          {fileParts.map((part, idx) => (
            <img
              key={idx}
              src={part.url}
              alt={part.filename || t('optibot.uploadedImage')}
              className="h-24 w-24 rounded-lg object-cover"
            />
          ))}
        </div>
      )}
      {text && (
        <div className="max-w-[85%] rounded-lg bg-primary px-4 py-2.5 text-sm text-primary-foreground">
          {text}
        </div>
      )}
    </div>
  );
}
